import { api, Response } from "~/src/lib/api";
import { Id } from "~/src/lib/api/id";
import { Industry } from "~/src/lib/api/industries";
import { Page } from "~/src/lib/api/pages";

/**
 * Company data.
 */
export type Company = Partial<{
  id: Id;
  createdAt: string;
  updatedAt: string;
  name: string;
  taxNumber: string;
  website: string;
  size: Sizes;
  industryId: string;
  industry: Industry;
  pages?: Page[];
  subscriptionQuota: {
    applicantPool: {
      limit: number;
      used: number;
    };
  };
}>;

/**
 * Company size options.
 */
export enum Sizes {
  "1-50" = "1-50",
  "51-200" = "51-200",
  "201-500" = "201-500",
  "501-1000" = "501-1000",
  "1001-5000" = "1001-5000",
  "5001-10000" = "5001-10000",
  "10001+" = "10001+",
}

/**
 * Get parameters.
 * @description In this particular case we also need to accept a string as well.
 * Because a use case of ours on the public job listing page, we need to pass a string with
 * the ids.
 */
type GetParams = {
  id: Id | string;
};

/**
 * Get the query key for the resource.
 * @see https://react-query.tanstack.com/guides/query-keys
 * @param params Query parameters.
 * @returns Query key.
 */
export const getQueryKey = (params?: GetParams) => {
  return ["companies", params].filter(Boolean);
};

/**
 * Get a single company by id.
 * @param id Id.
 * @returns A promise of the response.
 */
export const get = async ({ id }: GetParams) => {
  return (await api.get<Response<Company>>(`/companies/${id}`)).data;
};

/**
 * Create a new company.
 * @param data New company data.
 * @returns A promise of the response.
 */
export const create = async (data: Company) => {
  return (await api.post<Response<Company>>("/companies", data)).data;
};

/**
 * Update existing company.
 * @param company Existing company data with id.
 * @returns A promise of the response.
 */
export const update = async ({ id, ...data }: Company & GetParams) => {
  return (await api.patch<Response<Company>>(`/companies/${id}`, data)).data;
};

/**
 * Validate company data. Throws on violations.
 * @param data Company data.
 */
export const validate = async ({ id, ...data }: Company) => {
  await api.request({
    url: id ? `/companies/${id}` : "/companies",
    method: id ? "PATCH" : "POST",
    data,
    params: { validate: 1 },
  });
};
